import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { queueMilestoneMutation } from '../../utils/rockSaveQueue'
import { RockMilestone, User } from '../../types'
import UserAvatar from '../shared/UserAvatar'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  milestone: RockMilestone
  teamId: number
  rockId: number
  teamMembers: User[]
  /** Fires after a milestone mutation lands, for callers that hold their own copy of the rock. */
  onChanged?: () => void
  onBeforeChange?: () => void
}

function dateInputValue(dateStr: string | null): string {
  return dateStr ? dateStr.slice(0, 10) : ''
}

export default function MilestoneItem({ milestone, teamId, rockId, teamMembers, onChanged, onBeforeChange }: Props) {
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(milestone.title)
  const [assigneeId, setAssigneeId] = useState(milestone.assignee_id ? String(milestone.assignee_id) : '')
  const [dueDate, setDueDate] = useState(dateInputValue(milestone.due_date))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startEditing = () => {
    // Re-seed from the record each time the editor opens; drafts are never
    // re-initialized while it is open, so a background prop refresh (the
    // meeting's 10s heartbeat) can't wipe what is being typed.
    setTitle(milestone.title)
    setAssigneeId(milestone.assignee_id ? String(milestone.assignee_id) : '')
    setDueDate(dateInputValue(milestone.due_date))
    setError(null)
    setEditing(true)
  }

  const handleComplete = () => {
    if (saving) return
    onBeforeChange?.()
    setSaving(true)
    setError(null)
    queueMilestoneMutation('post', `/teams/${teamId}/rocks/${rockId}/milestones/${milestone.id}/complete`, {}, {
      onSuccess: () => { setSaving(false); onChanged?.() },
      onError: (_errors, message) => { setSaving(false); setError(message) },
    })
  }

  // Title, assignee and due date commit together on Save or Enter — never on
  // change. A native date input fires `change` as soon as each segment holds
  // any value, so a year typed digit by digit emits bogus dates like 0002-07-20.
  const handleUpdate = () => {
    if (!title.trim() || saving) return
    setSaving(true)
    setError(null)
    onBeforeChange?.()
    queueMilestoneMutation('patch', `/teams/${teamId}/rocks/${rockId}/milestones/${milestone.id}`, {
      milestone: {
        title: title.trim(),
        assignee_id: assigneeId || null,
        due_date: dueDate || null,
      }
    }, {
      onSuccess: () => { setSaving(false); setEditing(false); onChanged?.() },
      onError: (_errors, message) => { setSaving(false); setError(message) },
    })
  }

  const handleDelete = () => {
    if (saving) return
    onBeforeChange?.()
    setSaving(true)
    setError(null)
    queueMilestoneMutation('delete', `/teams/${teamId}/rocks/${rockId}/milestones/${milestone.id}`, {}, {
      onSuccess: () => { setSaving(false); onChanged?.() },
      onError: (_errors, message) => { setSaving(false); setError(message) },
    })
  }

  if (editing) {
    return (
      <div className="py-2 px-3 rounded-lg bg-[#F5F5F5]/40 space-y-2">
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); handleUpdate() }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditing(false) }
          }}
          aria-label="Milestone title"
          className="w-full px-2 py-1 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
          autoFocus
        />
        <div className="flex items-center gap-2">
          <select
            value={assigneeId}
            onChange={e => setAssigneeId(e.target.value)}
            aria-label="Milestone assignee"
            className="flex-1 min-w-0 px-2 py-1 border border-stone/30 rounded text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
          >
            <option value="">Unassigned</option>
            {sortPeople(teamMembers).map(member => (
              <option key={member.id} value={member.id}>{member.full_name}</option>
            ))}
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleUpdate() }
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditing(false) }
            }}
            aria-label="Milestone due date"
            className="px-2 py-1 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
          />
        </div>
        {error && <p role="alert" className="text-xs text-pinnacle-red">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUpdate}
            aria-disabled={saving}
            className={`text-sm text-pinnacle-red font-medium ${saving ? 'opacity-50' : ''}`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-sm text-text-muted">Cancel</button>
        </div>
      </div>
    )
  }

  return (
    <div className="py-1">
      <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#F5F5F5]/20 group">
        <button
          onClick={handleComplete}
          disabled={saving}
          aria-label={milestone.status === 'complete' ? `Reopen milestone ${milestone.title}` : `Complete milestone ${milestone.title}`}
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            milestone.status === 'complete'
              ? 'bg-[#648E8A] border-[#648E8A] text-white'
              : 'border-stone/40 hover:border-[#648E8A]'
          }`}
        >
          {milestone.status === 'complete' && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <button
            onClick={startEditing}
            aria-label={`Edit milestone ${milestone.title}`}
            className={`text-sm text-left ${milestone.status === 'complete' ? 'text-text-muted line-through' : 'text-text-primary'}`}
          >
            {milestone.title}
          </button>
        </div>

        {milestone.linked_action_item_id && (
          <Link
            href={`/teams/${teamId}/action_items?highlight=${milestone.linked_action_item_id}`}
            title="View linked action item"
            className="text-text-muted hover:text-[#648E8A] transition-colors flex-shrink-0"
            onClick={e => e.stopPropagation()}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </Link>
        )}

        {milestone.assignee && (
          <UserAvatar user={milestone.assignee} size="xs" />
        )}

        {milestone.due_date && (
          <span className="text-sm text-text-muted whitespace-nowrap">
            {new Date(milestone.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}

        <button
          onClick={handleDelete}
          aria-label={`Delete milestone ${milestone.title}`}
          className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-pinnacle-red transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {error && <p role="alert" className="px-3 text-xs text-pinnacle-red">{error}</p>}
    </div>
  )
}
